<template>
  <div class="card">
    <div class="card-header card-header-flex flex-column">
      <a-tabs class="kit-tabs" v-model="activeTab" @change="onTabChange">
        <a-tab-pane key="waiting" :tab="`${$t('pickup.waiting')} (${waiting.length})`" />
        <a-tab-pane key="history" :tab="$t('pickup.history')" />
      </a-tabs>
    </div>

    <div class="card-body">
      <!-- Filters (shared by both tabs) -->
      <div class="row mb-3">
        <div class="col-md-2">
          <a-select
            v-model="filters.level"
            style="width: 100%"
            allow-clear
            :placeholder="$t('pickup.level')"
            @change="onFiltersChange"
          >
            <a-select-option v-for="level in filterOptions.levels" :key="level" :value="level">
              {{ levelLabel(level) }}
            </a-select-option>
          </a-select>
        </div>
        <div class="col-md-2">
          <a-select
            v-model="filters.classRoom"
            style="width: 100%"
            allow-clear
            :placeholder="$t('pickup.classe')"
            @change="onFiltersChange"
          >
            <a-select-option
              v-for="classRoom in visibleClassRooms"
              :key="classRoom._id"
              :value="classRoom._id"
            >
              {{ classRoom.name }}
            </a-select-option>
          </a-select>
        </div>
        <div class="col-md-3">
          <a-input
            v-model="filters.studentName"
            allow-clear
            :placeholder="$t('pickup.student')"
            @change="onNameChange"
          />
        </div>
        <div class="col-md-3">
          <a-input
            v-model="filters.parentName"
            allow-clear
            :placeholder="$t('pickup.parent')"
            @change="onNameChange"
          />
        </div>
        <div class="col-md-2">
          <a-select v-model="filters.sort" style="width: 100%" @change="onFiltersChange">
            <a-select-option value="asc">{{ $t('pickup.sortOldest') }}</a-select-option>
            <a-select-option value="desc">{{ $t('pickup.sortNewest') }}</a-select-option>
          </a-select>
        </div>
      </div>

      <!-- Waiting list -->
      <a-table
        v-if="activeTab === 'waiting'"
        row-key="_id"
        :loading="loading"
        :columns="waitingColumns"
        :data-source="waiting"
        :pagination="false"
        :locale="{ emptyText: $t('pickup.noWaiting') }"
      >
        <template slot="requestedAt" slot-scope="text, record">
          {{ formatTime(record.createdAt) }}
        </template>
        <template slot="expiresIn" slot-scope="text, record">
          <a-tag color="orange">{{ minutesLeft(record) }} min</a-tag>
        </template>
        <template slot="action" slot-scope="text, record">
          <a-popconfirm
            :title="$t('pickup.confirmValidate')"
            @confirm="validate(record)"
          >
            <a-button type="primary" size="small">
              {{ $t("pickup.validate") }}
            </a-button>
          </a-popconfirm>
        </template>
      </a-table>

      <!-- History -->
      <div v-else>
        <div class="d-flex mb-3">
          <a-select
            v-model="historyStatus"
            style="width: 200px"
            allow-clear
            :placeholder="$t('pickup.status')"
            @change="loadHistory(1)"
          >
            <a-select-option value="picked_up">
              {{ $t("pickup.statusPickedUp") }}
            </a-select-option>
            <a-select-option value="expired">
              {{ $t("pickup.statusExpired") }}
            </a-select-option>
          </a-select>
          <a-range-picker class="ml-2" v-model="historyRange" @change="loadHistory(1)" />
        </div>
        <a-table
          row-key="_id"
          :loading="loading"
          :columns="historyColumns"
          :data-source="history.data"
          :pagination="{
            current: history.page,
            pageSize: history.limit,
            total: history.total,
          }"
          @change="(pagination) => loadHistory(pagination.current)"
        >
          <template slot="requestedAt" slot-scope="text, record">
            {{ formatDateTime(record.createdAt) }}
          </template>
          <template slot="status" slot-scope="text, record">
            <a-tag :color="record.status === 'picked_up' ? 'green' : 'red'">
              {{
                record.status === "picked_up"
                  ? $t("pickup.statusPickedUp")
                  : $t("pickup.statusExpired")
              }}
            </a-tag>
          </template>
          <template slot="validatedBy" slot-scope="text, record">
            <span v-if="record.validatedBy && record.validatedBy.name">
              {{ record.validatedBy.name }} – {{ formatTime(record.validatedAt) }}
            </span>
            <span v-else>-</span>
          </template>
        </a-table>
      </div>
    </div>
  </div>
</template>

<script>
import apiClient from '@/services/axios'
import moment from 'moment'
import { mapState } from 'vuex'
import { showApiError } from '@/utilities/showApiError'
import { getAllLevels } from '@/config/LevelConstants'

const DEFAULT_SORT = { waiting: 'asc', history: 'desc' }

export default {
  name: 'pickup',
  data() {
    return {
      activeTab: 'waiting',
      loading: false,
      waiting: [],
      history: { data: [], total: 0, page: 1, limit: 20 },
      historyStatus: undefined,
      historyRange: [],
      filters: {
        level: undefined,
        classRoom: undefined,
        studentName: '',
        parentName: '',
        sort: DEFAULT_SORT.waiting,
      },
      filterOptions: { classRooms: [], levels: [] },
      nameTimer: null,
      now: Date.now(),
      clock: null,
    }
  },
  computed: {
    ...mapState(['settings', 'user']),
    allLevels() {
      const building = this.user.building.find(
        (el) => el.dbName === this.settings.activeBuilding,
      )
      return getAllLevels(this.$t.bind(this), (building && building.countryCode) || 'TN')
    },
    visibleClassRooms() {
      if (!this.filters.level) return this.filterOptions.classRooms
      return this.filterOptions.classRooms.filter((c) => c.level === this.filters.level)
    },
    waitingColumns() {
      return [
        { title: this.$t('pickup.student'), dataIndex: 'studentName' },
        { title: this.$t('pickup.level'), dataIndex: 'level', customRender: this.levelLabel },
        { title: this.$t('pickup.classe'), dataIndex: 'classRoomName' },
        { title: this.$t('pickup.parent'), customRender: (text, record) => this.parentLabel(record) },
        { title: this.$t('pickup.distance'), dataIndex: 'distanceMeters', customRender: this.formatDistance },
        { title: this.$t('pickup.requestedAt'), scopedSlots: { customRender: 'requestedAt' } },
        { title: this.$t('pickup.expiresIn'), scopedSlots: { customRender: 'expiresIn' } },
        { title: '', scopedSlots: { customRender: 'action' } },
      ]
    },
    historyColumns() {
      return [
        { title: this.$t('pickup.student'), dataIndex: 'studentName' },
        { title: this.$t('pickup.level'), dataIndex: 'level', customRender: this.levelLabel },
        { title: this.$t('pickup.classe'), dataIndex: 'classRoomName' },
        { title: this.$t('pickup.parent'), customRender: (text, record) => this.parentLabel(record) },
        { title: this.$t('pickup.distance'), dataIndex: 'distanceMeters', customRender: this.formatDistance },
        { title: this.$t('pickup.requestedAt'), scopedSlots: { customRender: 'requestedAt' } },
        { title: this.$t('pickup.status'), scopedSlots: { customRender: 'status' } },
        { title: this.$t('pickup.validatedBy'), scopedSlots: { customRender: 'validatedBy' } },
      ]
    },
  },
  created() {
    this.loadFilterOptions()
    this.loadWaiting()
    this.$socket.client.on('pickup', this.onNewPickup)
    this.$socket.client.on('pickupUpdated', this.loadWaiting)
    this.clock = setInterval(() => {
      this.now = Date.now()
      this.waiting = this.waiting.filter((p) => this.minutesLeft(p) > 0)
    }, 30000)
  },
  beforeDestroy() {
    this.$socket.client.off('pickup', this.onNewPickup)
    this.$socket.client.off('pickupUpdated', this.loadWaiting)
    clearInterval(this.clock)
    clearTimeout(this.nameTimer)
  },
  methods: {
    levelLabel(level) {
      return (level && this.allLevels[level]) || level || '-'
    },
    parentLabel(pickup) {
      if (!pickup.parentType) return pickup.parentName
      return `${pickup.parentName} (${this.$t(`pickup.relation.${pickup.parentType}`)})`
    },
    formatDistance(meters) {
      if (typeof meters !== 'number') return '-'
      if (meters < 1000) return `${meters} m`
      return `${(meters / 1000).toFixed(1)} km`
    },
    filterParams() {
      return {
        level: this.filters.level,
        classRoom: this.filters.classRoom,
        studentName: this.filters.studentName || undefined,
        parentName: this.filters.parentName || undefined,
        sort: this.filters.sort,
      }
    },
    onFiltersChange() {
      if (
        this.filters.classRoom &&
        !this.visibleClassRooms.some((c) => c._id === this.filters.classRoom)
      ) {
        this.filters.classRoom = undefined
      }
      this.loadActiveTab()
    },
    onNameChange() {
      clearTimeout(this.nameTimer)
      this.nameTimer = setTimeout(this.loadActiveTab, 400)
    },
    async loadFilterOptions() {
      try {
        const { data } = await apiClient.get('/pickup/filters')
        this.filterOptions = data
      } catch (err) {
        showApiError(this, err)
      }
    },
    formatTime(date) {
      return date ? moment(date).format('HH:mm') : ''
    },
    formatDateTime(date) {
      return date ? moment(date).format('DD/MM/YYYY HH:mm') : ''
    },
    minutesLeft(pickup) {
      const ms = new Date(pickup.expiresAt).getTime() - this.now
      return Math.max(0, Math.ceil(ms / 60000))
    },
    onNewPickup() {
      this.loadFilterOptions()
      this.loadWaiting()
    },
    onTabChange() {
      this.filters.sort = DEFAULT_SORT[this.activeTab]
      this.loadActiveTab()
    },
    loadActiveTab() {
      if (this.activeTab === 'waiting') this.loadWaiting()
      else this.loadHistory(1)
    },
    async loadWaiting() {
      this.loading = true
      try {
        const { data } = await apiClient.get('/pickup/waiting', {
          params: this.filterParams(),
        })
        this.waiting = data
      } catch (err) {
        showApiError(this, err)
      } finally {
        this.loading = false
      }
    },
    async loadHistory(page) {
      this.loading = true
      try {
        const [from, to] = this.historyRange || []
        const { data } = await apiClient.get('/pickup/history', {
          params: {
            ...this.filterParams(),
            page,
            limit: this.history.limit,
            status: this.historyStatus,
            from: from ? from.startOf('day').toISOString() : undefined,
            to: to ? to.endOf('day').toISOString() : undefined,
          },
        })
        this.history = data
      } catch (err) {
        showApiError(this, err)
      } finally {
        this.loading = false
      }
    },
    async validate(pickup) {
      try {
        await apiClient.patch(`/pickup/${pickup._id}/validate`)
        this.$message.success(this.$t('pickup.validated'))
        this.loadWaiting()
      } catch (err) {
        showApiError(this, err)
        this.loadWaiting()
      }
    },
  },
}
</script>
